import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// CONFIRMATION INTEGREE A LA PAGE (refonte de l'Admin, phase 1) : remplace les fenetres du navigateur (confirm/alert).
// Une action risquee demande une confirmation qui dit CE QUI VA SE PASSER, dans la page, a cote de l'action.
//
// Usage, sans changer la forme du code appelant :
//   const [confirmer, confirmation] = useConfirm();
//   ...
//   if (!(await confirmer({ message: '…', details: ['…'], confirmLabel: '…' }))) return;
//   ...
//   return (<section>… {confirmation} …</section>);   // le bloc s'affiche ici, dans le flux de la page
//
// `confirmer` rend une promesse (true = confirme, false = annule ou Echap). Une seule confirmation a la fois : en demander
// une seconde annule la premiere (elle vaut false).

function ConfirmationInline({ options, onAnswer }) {
  const { t } = useTranslation('admin');
  const cancelRef = useRef(null);
  const blocRef = useRef(null);

  useEffect(() => {
    // Le focus va sur « Annuler » : valider par erreur avec Entree ne supprime rien.
    if (cancelRef.current) cancelRef.current.focus();
    if (blocRef.current && blocRef.current.scrollIntoView) blocRef.current.scrollIntoView({ block: 'nearest' });
  }, []);

  const surTouche = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); onAnswer(false); }
  };

  const titre = options.title || t('confirm.defaultTitle');
  return (
    <div
      ref={blocRef}
      className={`adm-confirm${options.danger === false ? ' is-neutral' : ''}`}
      role="alertdialog"
      aria-labelledby="adm-confirm-titre"
      aria-describedby="adm-confirm-texte"
      onKeyDown={surTouche}
    >
      <b id="adm-confirm-titre">{titre}</b>
      <div id="adm-confirm-texte" className="adm-confirm-texte">
        {options.message && <p>{options.message}</p>}
        {Array.isArray(options.details) && options.details.length > 0 && (
          <ul>
            {options.details.map((ligne) => <li key={ligne}>{ligne}</li>)}
          </ul>
        )}
      </div>
      <div className="adm-confirm-actions">
        <button type="button" className={`btn btn-sm ${options.danger === false ? 'btn-primary' : 'adm-btn-danger'}`} onClick={() => onAnswer(true)}>
          {options.confirmLabel || t('confirm.yes')}
        </button>
        <button type="button" ref={cancelRef} className="btn btn-outline btn-sm" onClick={() => onAnswer(false)}>
          {options.cancelLabel || t('confirm.no')}
        </button>
      </div>
    </div>
  );
}

export function useConfirm() {
  const [demande, setDemande] = useState(null); // { options, resolve }
  const enCours = useRef(null);

  const repondre = useCallback((valeur) => {
    const courante = enCours.current;
    enCours.current = null;
    setDemande(null);
    if (courante) courante.resolve(valeur);
  }, []);

  const confirmer = useCallback((options = {}) => new Promise((resolve) => {
    if (enCours.current) enCours.current.resolve(false);
    enCours.current = { resolve };
    setDemande({ options });
  }), []);

  // Si le composant disparait pendant une confirmation (changement de section), la promesse ne reste pas pendante.
  useEffect(() => () => { if (enCours.current) enCours.current.resolve(false); }, []);

  const element = demande ? <ConfirmationInline options={demande.options} onAnswer={repondre} /> : null;
  return [confirmer, element];
}

export default useConfirm;
